// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./DesignCategoriesEditAdd.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import {
	deleteAPICall,
	postAPICall,
	putAPICall,
} from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
} from "@mui/material";

// MUI ICONS
import CloseIcon from "@mui/icons-material/Close";

// REACT COMPONENT
const DesignCategoriesEditAdd = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [loading, setLoading] = useState(false);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	const [categoryTitle, setCategoryTitle] = useState("");

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		if (props.open && props.editing && props.recordToEdit) {
			setCategoryTitle(props.recordToEdit.title);
		}
	}, [props.open, props.recordToEdit]);

	// INPUT HANDLERS
	const categoryTitleChanged = (event) => {
		setCategoryTitle(event.target.value);
	};

	// API FUNCTIONS
	const handleUpdateRecord = async () => {
		await putAPICall(instance, accounts[0], "/api/encompass/designCategories/update", {
			id: props.recordToEdit.id,
			title: categoryTitle,
			modifiedBy: employeeID,
		});
	};

	const handleAddRecord = async () => {
		await postAPICall(instance, accounts[0], "/api/encompass/designCategories/add", {
			title: categoryTitle,
			modifiedBy: employeeID,
		});
	};

	const handleDeleteRecord = async () => {
		await deleteAPICall(instance, accounts[0], "/api/encompass/designCategories/delete", {
			id: props.recordToEdit.id,
		});
	};

	// HELPER FUNCTIONS
	const handleSubmitClick = async () => {
		if (!validateInputFields()) return;
		setLoading(true);
		try {
			if (props.editing) {
				await handleUpdateRecord();
			} else {
				await handleAddRecord();
			}
		} catch (err) {
			console.error(err);
		}
		setAlertMessage("");
		setAlertOpen(false);
		setLoading(false);
		props.onClose(true, !props.editing ? categoryTitle : null);
	};

	const handleCancelClick = () => {
		setAlertMessage("");
		setAlertOpen(false);
		props.onClose(false);
	};

	const handleDeleteClick = async () => {
		setLoading(true);
		await handleDeleteRecord();
		setAlertMessage("");
		setAlertOpen(false);
		setLoading(false);
		props.onClose(true);
	};

	const resetInputFields = () => {
		setCategoryTitle("");
	};

	const validateInputFields = () => {
		if (categoryTitle === "") {
			setAlertMessage("Category title is required");
			setAlertOpen(true);
			return false;
		}
		return true;
	};

	// RENDER
	return (
		<Dialog
			id="DesignCategoriesEditAddDialog"
			open={props.open}
			fullWidth
			maxWidth="sm"
			onClose={handleCancelClick}
			TransitionProps={{ onExited: resetInputFields }}
		>
			<DialogTitle id="DesignCategoriesEditAddDialogTitle">
				{props.editing ? "Edit Design Category" : "Add Design Category"}
				{props.editing && (
					<Button
						color="error"
						variant="contained"
						size="small"
						onClick={handleDeleteClick}
					>
						Delete
					</Button>
				)}
			</DialogTitle>

			<DialogContent id="DesignCategoriesEditAddDialogContent">
				{loading ? (
					<div className="editDesignCategoriesLoadingContainer">
						<CircularProgress />
					</div>
				) : (
					<>
						{alertOpen && (
							<Alert
								severity="warning"
								sx={{ mb: 2 }}
								action={
									<IconButton
										aria-label="close"
										color="inherit"
										size="small"
										onClick={() => setAlertOpen(false)}
									>
										<CloseIcon />
									</IconButton>
								}
							>
								{alertMessage}
							</Alert>
						)}

						<TextField
							id="categoryTitle"
							label="Category Title"
							variant="outlined"
							value={categoryTitle}
							onChange={categoryTitleChanged}
							fullWidth
						/>

						<div className="editDesignCategoriesButtons" style={{ marginTop: "1em" }}>
							<Button
								variant="outlined"
								color="error"
								onClick={handleCancelClick}
							>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmitClick}
							>
								Submit
							</Button>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

// EXPORT
export default DesignCategoriesEditAdd;
